import {
  clearAnalySessionId,
  clearAnalySessionIdIf,
  loadAnalySessionId,
} from './analySessionStore';
import { purgeDeviceRecordings } from './purgeDeviceRecordings';
import { purgeDiagnoses } from './purgeDiagnoses';
import {
  clearDiagnoses,
  clearProgress,
  loadDiagnoses,
  removeDiagnosis,
} from '../record';

import type { PurgeResult } from './purgeDiagnoses';
import type { DiagnosisRecord } from '../record';

/**
 * deleteAllDiagnosisData — MY "진단 데이터 모두 삭제". 서버를 비우고(`purgeDiagnoses`) 그 결과에
 * 맞춰 로컬을 정리한다. 화면은 이 결과만 토스트로 옮긴다(RN 셸 규칙: 판정은 순수 모듈에).
 *
 * - `done` — 서버에 남은 것이 없다. 이력·진행 중 진단·세션 id 를 전부 비운다.
 * - `partial` — 일부가 실패했다. **로컬을 전부 비우면 안 된다**: 서버에 남은 회차가 다음 부팅의
 *   `restoreDiagnoses`로 되살아나 사용자는 지운 줄 안 것이 돌아온다. 서버에서 사라진 회차만
 *   로컬에서 지운다. 실패분은 남은 채라 다시 눌러 이어 지울 수 있다(404 는 성공).
 * - `failed` — 목록을 못 모았다(오프라인·세션 미확보). 아무것도 지우지 않았다.
 *
 * 기기에 남은 녹음 파일(`purgeRecordings`)은 서버 삭제가 끝난 뒤 `done`·`partial` 모두에서 한 번
 * 비운다 — 서버 사본과 무관한 로컬 잔여물이다. `failed`(아무것도 못 지움)에서는 부르지 않는다.
 * 실패(옛 빌드의 `Unknown method`·타임아웃)는 삼킨다: 파일은 다음 앱 시작의 `purgeOrphans` 가 지우므로
 * 결과·토스트를 바꿀 이유가 없다.
 *
 * 부분 실패여도 **진행 중 회차의 서버 사본이 지워졌다면** 그 세션 id 와 진행 상태는 비운다 —
 * 남기면 이어하기가 이미 없는 서버 루트로 제출한다.
 */

export type DeleteAllOutcome = 'done' | 'partial' | 'failed';

export interface DeleteAllDeps {
  purge: () => Promise<PurgeResult>;
  /** 기기 녹음 파일 비우기. 브라우저에서는 아무것도 안 한다. reject 는 호출부가 삼킨다. */
  purgeRecordings: () => Promise<void>;
  loadRecords: () => DiagnosisRecord[];
  removeRecord: (id: string) => void;
  clearAllRecords: () => void;
  clearProgress: () => void;
  loadSessionId: () => string | null;
  clearSessionId: () => void;
  clearSessionIdIf: (id: string) => void;
}

const defaultDeps = (): DeleteAllDeps => ({
  purge: () => purgeDiagnoses(),
  purgeRecordings: () => purgeDeviceRecordings(),
  loadRecords: loadDiagnoses,
  removeRecord: removeDiagnosis,
  clearAllRecords: clearDiagnoses,
  clearProgress,
  loadSessionId: loadAnalySessionId,
  clearSessionId: clearAnalySessionId,
  clearSessionIdIf: clearAnalySessionIdIf,
});

export const deleteAllDiagnosisData = async (
  deps: DeleteAllDeps = defaultDeps(),
): Promise<DeleteAllOutcome> => {
  const result = await deps.purge();
  if (result.listFailed) {
    return 'failed';
  }

  try {
    await deps.purgeRecordings();
  } catch {
    /* 못 지워도 다음 앱 시작이 지운다 */
  }

  if (result.apiOff || result.failed.length === 0) {
    deps.clearAllRecords();
    deps.clearProgress();
    deps.clearSessionId();
    return 'done';
  }

  // 일부만 지웠다 — `analyId` 가 없는 옛 기록은 서버에 짝이 없어 어느 쪽이 지워졌는지 알 수 없으니
  // 로컬에 남긴다(의도). 다시 시도하면 전부 지워지는 'done' 경로가 이들도 비운다.
  const deleted = new Set(result.deleted);
  deps
    .loadRecords()
    .filter((record) => record.analyId && deleted.has(record.analyId))
    .forEach((record) => deps.removeRecord(record.id));

  const sessionId = deps.loadSessionId();
  if (sessionId && deleted.has(sessionId)) {
    deps.clearSessionIdIf(sessionId);
    deps.clearProgress();
  }
  return 'partial';
};
